import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  addMembership,
  applyRelationshipMigration,
  assignmentValues,
  constraintAttempt,
  initialMembershipRemoval,
  initialMigration,
  initialProjectCreation,
  inspectAssignment,
  joinRelationships,
  membershipRemoval,
  projectCreationStep,
  relationshipJoinSql,
  relationshipMemberships,
  relationshipTasks,
  renameProject,
  type AssignmentExample,
  type AssignmentRule,
  type FactDesign,
  type JoinKind,
  type JoinPath,
  type MembershipKey,
} from './relationship-models';

import './relationship-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
}: {
  label: string;
  columns: string[];
  rows: (string | number | null)[][];
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column} data-null={cell === null || undefined}>
                  {cell ?? 'NULL'}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Summary({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function RelationshipDuplicationLab() {
  const [design, setDesign] = useState<FactDesign>('copies');
  const [renamed, setRenamed] = useState(false);
  const frame = renameProject(design, renamed);
  return (
    <Lab
      title="One project, two copies of its name"
      eyebrow="Relationships · repeated facts"
      className="rel-lab"
    >
      <Predict>
        If two tasks store the same project name, will changing one copy rename the project
        everywhere?
      </Predict>
      <label className="rel-control">
        Where is the project name stored?
        <ModelSelect
          aria-label="Project name storage"
          value={design}
          onValueChange={(value) => {
            setDesign(value as FactDesign);
            setRenamed(false);
          }}
          options={[
            { value: 'copies', label: 'Copy the name into each task' },
            { value: 'reference', label: 'Store one project; tasks keep its ID' },
          ]}
        />
      </label>
      <div className="cb-two">
        <Panel label="Stored records">
          {design === 'reference' && (
            <Table
              label="Project record"
              columns={['id', 'name']}
              rows={[[1, frame.projectName]]}
            />
          )}
          <Table
            label="Task storage"
            columns={['task id', design === 'reference' ? 'project_id' : 'project name']}
            rows={[
              [9, design === 'reference' ? 1 : (frame.taskNames[0] ?? '')],
              [10, design === 'reference' ? 1 : (frame.taskNames[1] ?? '')],
            ]}
          />
        </Panel>
        <Panel label="Names shown beside each task">
          <Table
            label="Displayed project names"
            columns={['task id', 'project name']}
            rows={[
              [9, frame.taskNames[0] ?? ''],
              [10, frame.taskNames[1] ?? ''],
            ]}
          />
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" disabled={renamed} onClick={() => setRenamed(true)}>
          {design === 'copies' ? 'Rename the first copy' : 'Rename project #1'}
        </Button>
        <Reset
          onClick={() => {
            setDesign('copies');
            setRenamed(false);
          }}
        />
      </div>
      <Result tone={frame.inconsistent ? 'warning' : renamed ? 'success' : 'neutral'}>
        {renamed
          ? frame.inconsistent
            ? 'The names disagree. A change to one copied field does not update the other task.'
            : 'Both tasks now show Apollo Demo. Their project IDs stayed 1; the name changed in one project record.'
          : 'Both tasks belong to Apollo. Try renaming it, then compare the other storage design.'}
      </Result>
    </Lab>
  );
}

export function RelationshipMembershipLab() {
  const [key, setKey] = useState<MembershipKey>('user');
  const [project, setProject] = useState('2');
  const initial = () =>
    relationshipMemberships
      .filter((row) => !(row.projectId === 1 && row.userId === 2))
      .map((row) => ({ ...row }));
  const [rows, setRows] = useState(initial);
  const [result, setResult] = useState<{ accepted: boolean; message: string } | null>(null);
  const resetAttempt = () => {
    setRows(initial());
    setResult(null);
  };
  return (
    <Lab
      title="One person can belong to several projects"
      eyebrow="Relationships · a key made of two columns"
      className="rel-lab"
    >
      <Predict>
        Maya already belongs to Apollo. Which key lets her join Support, while preventing the same
        membership twice?
      </Predict>
      <div className="cb-controls">
        <label>
          Membership primary key
          <ModelSelect
            aria-label="Membership primary key"
            value={key}
            onValueChange={(value) => {
              setKey(value as MembershipKey);
              resetAttempt();
            }}
            options={[
              { value: 'user', label: 'user_id only' },
              { value: 'pair', label: '(project_id, user_id) together' },
            ]}
          />
        </label>
        <label>
          Add Maya to
          <ModelSelect
            aria-label="Membership target project"
            value={project}
            onValueChange={(value) => {
              setProject(value);
              setResult(null);
            }}
            options={[
              { value: '2', label: 'Support · project 2' },
              { value: '1', label: 'Apollo · project 1' },
            ]}
          />
        </label>
      </div>
      <Table
        label="Membership records"
        columns={['project_id', 'user_id', 'role']}
        rows={rows.map((row) => [row.projectId, row.userId, row.role])}
      />
      <p className="rel-caption">
        Start with Maya (#1) in Apollo and Ravi (#2) in Support. A membership connects one user to
        one project.
      </p>
      <div className="cb-controls">
        <Button
          size="sm"
          onClick={() => {
            const next = addMembership(rows, Number(project), 1, key);
            setRows(next.rows);
            setResult(next);
          }}
        >
          Add membership
        </Button>
        <Reset
          onClick={() => {
            setKey('user');
            setProject('2');
            resetAttempt();
          }}
        />
      </div>
      <Result tone={result ? (result.accepted ? 'success' : 'warning') : 'neutral'}>
        {result?.message ??
          'Two memberships exist. Try a second project for Maya, then try adding the same pair again.'}
      </Result>
    </Lab>
  );
}

export function RelationshipJoinsLab() {
  const [kind, setKind] = useState<JoinKind>('inner');
  const [path, setPath] = useState<JoinPath>('assignee');
  const [queried, setQueried] = useState(false);
  const rows = joinRelationships(kind, path);
  const stored = relationshipTasks();
  return (
    <Lab
      title="A join follows the relationship you ask for"
      eyebrow="Relationships · missing and repeated result rows"
      className="rel-lab"
    >
      <Predict>
        Will every task appear? If you join each task to all project members, will you still get one
        row per task?
      </Predict>
      <div className="cb-controls">
        <label>
          Keep unmatched tasks?
          <ModelSelect
            aria-label="Relationship join type"
            value={kind}
            onValueChange={(value) => {
              setKind(value as JoinKind);
              setQueried(false);
            }}
            options={[
              { value: 'inner', label: 'INNER JOIN · matching rows only' },
              { value: 'left', label: 'LEFT JOIN · keep every task' },
            ]}
          />
        </label>
        <label>
          Which people?
          <ModelSelect
            aria-label="Relationship join path"
            value={path}
            onValueChange={(value) => {
              setPath(value as JoinPath);
              setQueried(false);
            }}
            options={[
              { value: 'assignee', label: 'The task’s assignee' },
              { value: 'members', label: 'All members of the task’s project' },
            ]}
          />
        </label>
      </div>
      <Table
        label="Tasks before join"
        columns={['id', 'project_id', 'assignee_id']}
        rows={stored.map((task) => [task.id, task.projectId, task.assigneeId])}
      />
      <p className="rel-caption">
        Apollo (#1): Maya and Ravi. Support (#2): Ravi. Tasks #1, #4 and #8 have no project; task
        #10 has no assignee.
      </p>
      <pre>
        <code>{relationshipJoinSql(kind, path)}</code>
      </pre>
      <div className="cb-controls">
        <Button size="sm" onClick={() => setQueried(true)}>
          Show query result
        </Button>
        <Reset
          onClick={() => {
            setKind('inner');
            setPath('assignee');
            setQueried(false);
          }}
        />
      </div>
      {queried && (
        <Table
          label="Joined result"
          columns={['task id', 'project', path === 'assignee' ? 'assignee' : 'member']}
          rows={rows.map((row) => [row.taskId, row.project, row.person])}
        />
      )}
      <Result>
        {queried
          ? `${rows.length} result row${rows.length === 1 ? '' : 's'}; the ${stored.length} stored tasks are unchanged. ${path === 'members' ? 'Each task appears once per member of its project. These people are not necessarily the task’s assignee.' : kind === 'inner' ? 'Tasks #1, #4, #8 and #10 disappear from this result: #1, #4 and #8 have no project and #10 has no assignee.' : `All ${stored.length} tasks remain. NULL means this joined value has no matching record.`}`
          : 'Choose the relationship, make your prediction, then show the result.'}
      </Result>
    </Lab>
  );
}

const assignmentOptions = [
  { value: 'other-project', label: 'Apollo + Jo · not a member' },
  { value: 'member', label: 'Apollo + Ravi · member of Apollo' },
  { value: 'project-only', label: 'Apollo + no assignee' },
  { value: 'personal', label: 'No project + no assignee' },
  { value: 'assignee-only', label: 'No project + Ravi' },
  { value: 'missing-project', label: 'Missing project 999 + no assignee' },
];
export function RelationshipAssignmentLab() {
  const [rule, setRule] = useState<AssignmentRule>('separate');
  const [example, setExample] = useState<AssignmentExample>('other-project');
  const [checked, setChecked] = useState(false);
  const values = assignmentValues(example);
  const result = inspectAssignment(values, rule);
  return (
    <Lab
      title="An existing user may still be the wrong assignee"
      eyebrow="Relationships · validate the pair"
      className="rel-lab"
    >
      <Predict>
        Jo exists, and Apollo exists. Does that prove Jo belongs to Apollo? Try the missing-value
        cases too.
      </Predict>
      <div className="cb-controls">
        <label>
          Assignment rule
          <ModelSelect
            aria-label="Assignment relationship rule"
            value={rule}
            onValueChange={(value) => {
              setRule(value as AssignmentRule);
              setChecked(false);
            }}
            options={[
              { value: 'separate', label: 'Check project and user separately' },
              { value: 'membership', label: 'Also require their membership pair' },
            ]}
          />
        </label>
        <label>
          Values to save
          <ModelSelect
            aria-label="Assignment example"
            value={example}
            onValueChange={(value) => {
              setExample(value as AssignmentExample);
              setChecked(false);
            }}
            options={assignmentOptions}
          />
        </label>
      </div>
      <div className="cb-two">
        <Panel label="Task values to check">
          <Table
            label="Assignment values"
            columns={['project_id', 'assignee_id']}
            rows={[[values.projectId, values.assigneeId]]}
          />
        </Panel>
        <Panel label="Existing memberships">
          <Table
            label="Assignment memberships"
            columns={['project_id', 'user_id']}
            rows={relationshipMemberships.map((row) => [row.projectId, row.userId])}
          />
        </Panel>
      </div>
      <p className="rel-caption">
        Both designs also check that a project exists and that an assignee cannot be set without a
        project. The second design adds a foreign key for the two values together.
      </p>
      <div className="cb-controls">
        <Button size="sm" onClick={() => setChecked(true)}>
          Check assignment
        </Button>
        <Reset
          onClick={() => {
            setRule('separate');
            setExample('other-project');
            setChecked(false);
          }}
        />
      </div>
      <Result
        tone={
          checked
            ? result.accepted && !(rule === 'separate' && example === 'other-project')
              ? 'success'
              : 'warning'
            : 'neutral'
        }
      >
        {checked ? result.message : 'Compare the values with the membership pairs before checking.'}
      </Result>
    </Lab>
  );
}

export function SchemaConstraintsLab() {
  const [layer, setLayer] = useState<'python' | 'database'>('python');
  const [entry, setEntry] = useState<'api' | 'sql'>('api');
  const [attempted, setAttempted] = useState(false);
  const result = constraintAttempt(layer, entry);
  return (
    <Lab
      title="A rule must protect every writer"
      eyebrow="Schema · where a rule is enforced"
      className="rel-lab"
    >
      <Predict>
        The API rejects Jo as an assignee for Apollo. What happens if another program writes
        directly to the database?
      </Predict>
      <div className="cb-controls">
        <label>
          Where is the membership rule?
          <ModelSelect
            aria-label="Constraint enforcement"
            value={layer}
            onValueChange={(value) => {
              setLayer(value as typeof layer);
              setAttempted(false);
            }}
            options={[
              { value: 'python', label: 'Python check only' },
              { value: 'database', label: 'Database foreign key too' },
            ]}
          />
        </label>
        <label>
          Write path
          <ModelSelect
            aria-label="Constraint write path"
            value={entry}
            onValueChange={(value) => {
              setEntry(value as typeof entry);
              setAttempted(false);
            }}
            options={[
              { value: 'api', label: 'Through the API' },
              { value: 'sql', label: 'Direct database connection' },
            ]}
          />
        </label>
      </div>
      <div className="rel-flow" aria-label="Write path">
        <span>{entry === 'api' ? 'API request' : 'Direct SQL'}</span>
        <span aria-hidden="true">→</span>
        <span>{entry === 'api' ? 'Python checks' : 'No Python checks'}</span>
        <span aria-hidden="true">→</span>
        <span>{layer === 'database' ? 'Database checks pair' : 'Database checks user only'}</span>
      </div>
      <pre>
        <code>
          {'UPDATE tasks SET assignee_id = 3\nWHERE id = 10; -- task #10 belongs to Apollo (#1)'}
        </code>
      </pre>
      <div className="cb-two">
        <Panel label="Before write">
          <Table
            label="Constraint before"
            columns={['task id', 'project_id', 'assignee_id']}
            rows={[[10, 1, null]]}
          />
        </Panel>
        <Panel label="After write">
          {attempted ? (
            <Table
              label="Constraint after"
              columns={['task id', 'project_id', 'assignee_id']}
              rows={[[10, 1, result.accepted ? 3 : null]]}
            />
          ) : (
            <p>Predict, then try the write.</p>
          )}
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" onClick={() => setAttempted(true)}>
          Try assignment write
        </Button>
        <Reset
          onClick={() => {
            setLayer('python');
            setEntry('api');
            setAttempted(false);
          }}
        />
      </div>
      <Result tone={attempted ? (result.accepted ? 'warning' : 'success') : 'neutral'}>
        {attempted
          ? result.message
          : 'Jo (#3) has no membership in Apollo. The intended assignment is invalid.'}
      </Result>
    </Lab>
  );
}

export function SchemaMigrationLab() {
  const [atomic, setAtomic] = useState('yes');
  const [failure, setFailure] = useState('yes');
  const [state, setState] = useState(initialMigration);
  const [message, setMessage] = useState(
    'The old tasks table has 3 columns. No relationship migration is recorded.',
  );
  const [outcome, setOutcome] = useState<'applied' | 'failed' | 'skipped' | null>(null);
  const resetRun = () => {
    setState(initialMigration());
    setOutcome(null);
    setMessage('The old tasks table has 3 columns. No relationship migration is recorded.');
  };
  return (
    <Lab
      title="Change the schema without losing old tasks"
      eyebrow="Schema · migration and version history"
      className="rel-lab"
    >
      <Predict>
        If a statement fails after adding columns, what should remain? After success, what should a
        second run do?
      </Predict>
      <div className="cb-controls">
        <label>
          Commit boundary
          <ModelSelect
            aria-label="Migration commit boundary"
            value={atomic}
            onValueChange={(value) => {
              setAtomic(value);
              resetRun();
            }}
            options={[
              { value: 'yes', label: 'Schema and history in one transaction' },
              { value: 'no', label: 'Commit schema before history' },
            ]}
          />
        </label>
        <label>
          Later statement
          <ModelSelect
            aria-label="Migration later statement"
            value={failure}
            onValueChange={setFailure}
            options={[
              { value: 'yes', label: 'Fails after adding columns' },
              { value: 'no', label: 'Succeeds' },
            ]}
          />
        </label>
      </div>
      <ol className="rel-steps">
        <li>Check version 2</li>
        <li>Add tables and columns</li>
        <li>Run remaining statements</li>
        <li>Record version 2</li>
        <li>Commit</li>
      </ol>
      <div className="cb-two">
        <Panel label="Current saved schema">
          <Summary
            values={[
              [
                'Task columns',
                state.relationships
                  ? 'id, title, completed, project_id, assignee_id'
                  : 'id, title, completed',
              ],
              [
                'Relationship tables',
                state.relationships ? 'users, projects, memberships' : 'None',
              ],
              ['Recorded versions', state.history.length ? state.history.join(', ') : 'None'],
            ]}
          />
        </Panel>
        <Panel label="Existing task rows">
          <Table
            label="Migration old tasks"
            columns={
              state.relationships
                ? ['id', 'title', 'completed', 'project_id', 'assignee_id']
                : ['id', 'title', 'completed']
            }
            rows={[
              [1, 'Prepare the demo', 'false', ...(state.relationships ? [null, null] : [])],
              [4, "Read Maya's notes", 'true', ...(state.relationships ? [null, null] : [])],
              [8, 'Check the examples', 'false', ...(state.relationships ? [null, null] : [])],
            ]}
          />
        </Panel>
      </div>
      <div className="cb-controls">
        <Button
          size="sm"
          onClick={() => {
            const next = applyRelationshipMigration(state, atomic === 'yes', failure === 'yes');
            setState(next.state);
            setMessage(next.message);
            setOutcome(next.outcome);
          }}
        >
          Apply migration
        </Button>
        <Reset
          onClick={() => {
            setAtomic('yes');
            setFailure('yes');
            resetRun();
          }}
        />
      </div>
      <Result tone={outcome === 'failed' ? 'warning' : outcome ? 'success' : 'neutral'}>
        {message}
      </Result>
      <p className="rel-caption">
        New relationship columns allow NULL so existing tasks remain valid without invented project
        or assignee values. Version 2 represents this one ordered migration.
      </p>
    </Lab>
  );
}

export function SchemaTransactionLab() {
  const [atomic, setAtomic] = useState('no');
  const [manager, setManager] = useState('missing');
  const [state, setState] = useState(initialProjectCreation);
  return (
    <Lab
      title="A new project needs its first manager"
      eyebrow="Transactions · one operation, two records"
      className="rel-lab"
    >
      <Predict>
        If adding the manager fails, should an empty project remain? Compare two commits with one
        transaction.
      </Predict>
      <div className="cb-controls">
        <label>
          Save design
          <ModelSelect
            aria-label="Project creation commit boundary"
            value={atomic}
            onValueChange={(value) => {
              setAtomic(value);
              setState(initialProjectCreation());
            }}
            options={[
              { value: 'no', label: 'Commit each insert separately' },
              { value: 'yes', label: 'Commit both inserts together' },
            ]}
          />
        </label>
        <label>
          Manager user
          <ModelSelect
            aria-label="Project creation manager"
            value={manager}
            onValueChange={(value) => {
              setManager(value);
              setState(initialProjectCreation());
            }}
            options={[
              { value: 'missing', label: 'User 999 · does not exist' },
              { value: 'existing', label: 'Maya · existing user 1' },
            ]}
          />
        </label>
      </div>
      <div className="cb-two">
        <Panel label="Pending on this connection">
          <Table
            label="Pending project creation"
            columns={['record', 'present?']}
            rows={[
              ['project', state.pendingProject ? 'yes' : 'no'],
              ['manager membership', state.pendingMembership ? 'yes' : 'no'],
            ]}
          />
        </Panel>
        <Panel label="Committed · visible to another connection">
          <Table
            label="Committed project creation"
            columns={['record', 'present?']}
            rows={[
              ['project', state.storedProject ? 'yes' : 'no'],
              ['manager membership', state.storedMembership ? 'yes' : 'no'],
            ]}
          />
        </Panel>
      </div>
      <div className="cb-controls">
        <Button
          size="sm"
          disabled={state.phase === 'failed' || state.phase === 'done'}
          onClick={() =>
            setState(projectCreationStep(state, atomic === 'yes', manager === 'existing'))
          }
        >
          {state.phase === 'ready'
            ? 'Insert project'
            : state.phase === 'project-added'
              ? 'Insert manager membership'
              : 'Finish operation'}
        </Button>
        <Reset
          onClick={() => {
            setAtomic('no');
            setManager('missing');
            setState(initialProjectCreation());
          }}
        />
      </div>
      <Result
        tone={state.phase === 'failed' ? 'warning' : state.phase === 'done' ? 'success' : 'neutral'}
      >
        {state.message}
      </Result>
      <p className="rel-caption">
        “Manager” is a membership label here. This operation creates the first manager; it does not
        enforce sign-in permissions or a rule that every project must always retain a manager.
      </p>
    </Lab>
  );
}

export function SchemaDeleteLab() {
  const [state, setState] = useState(initialMembershipRemoval);
  return (
    <Lab
      title="Remove a membership without deleting its tasks"
      eyebrow="Relationships · deliberate removal"
      className="rel-lab"
    >
      <Predict>
        Task #9 still points to Ravi’s membership. Should removing that membership silently delete
        the task?
      </Predict>
      <div className="cb-two">
        <Panel label="Task record">
          <Table
            label="Removal task"
            columns={['task id', 'project_id', 'assignee_id']}
            rows={[[9, 1, state.assigned ? 2 : null]]}
          />
        </Panel>
        <Panel label="Membership record">
          <Table
            label="Removal membership"
            columns={['project_id', 'user_id']}
            rows={state.membership ? [[1, 2]] : []}
          />
        </Panel>
      </div>
      <pre>
        <code>
          {
            'FOREIGN KEY (project_id, assignee_id)\nREFERENCES memberships (project_id, user_id)\nON DELETE RESTRICT'
          }
        </code>
      </pre>
      <div className="cb-controls">
        <Button
          size="sm"
          disabled={!state.membership}
          onClick={() => setState(membershipRemoval(state, 'remove'))}
        >
          Remove membership
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!state.assigned}
          onClick={() => setState(membershipRemoval(state, 'unassign'))}
        >
          Unassign task #9
        </Button>
        <Reset onClick={() => setState(initialMembershipRemoval())} />
      </div>
      <Result>{state.message}</Result>
      <p className="rel-caption">
        Ravi’s user record and project Apollo remain throughout. Removing a membership removes only
        that connection.
      </p>
    </Lab>
  );
}
